/**
 * Página de Recetas - Versión Inmersiva (Awwwards Style)
 * Diseño inmersivo con animaciones GSAP y estilo premium
 * 
 * Según estructura.md - Página Recetas:
 * - Hero con imagen de plato apetecible
 * - Título: "Recetas para disfrutar cada día"
 * - Intro: "Descubre preparaciones tradicionales..."
 * - Grid: Cards con imagen, título y botón "Ver receta"
 * 
 * Según correciones.md:
 * - Fechas: 3 recetas de 2024 y 3 de 2025
 * - Subtítulos: Unificar en todas las recetas (ponerlos en todas o ninguna)
 */

import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ImmersiveLayout from '../components/layout/ImmersiveLayout';
import { AnimatedSection, MagneticButton } from '../components/animations';
import { getAllRecipes } from '../data/recipesData';
import RecipeSubmissionModal from '../components/recipes/RecipeSubmissionModal';
import useRecipeSubmissionModal from '../hooks/useRecipeSubmissionModal';

gsap.registerPlugin(ScrollTrigger);

/**
 * Componente de tarjeta de receta con estilo Awwwards
 * Según recetas.md: imagen destacada, título y botón "Ver receta"
 * @param {Object} props - Propiedades de la receta
 */
const RecipeCardImmersive = ({ 
  title, 
  imageUrl, 
  imageAlt, 
  url 
}) => {
  return (
    <Link to={url} className="group block h-full">
      <article className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 h-full flex flex-col relative">
        {/* Imagen destacada de la receta */}
        <div className="relative aspect-[4/3] bg-intercasa-bone overflow-hidden flex-shrink-0">
          {imageUrl ? (
            <img 
              src={imageUrl} 
              alt={imageAlt || title} 
              className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out"
            />
          ) : (
            /* Placeholder elegante */
            <div className="relative w-full h-full flex items-center justify-center bg-gradient-to-br from-intercasa-bone to-white">
              <svg 
                className="w-16 h-16 text-intercasa-red/20" 
                fill="currentColor" 
                viewBox="0 0 24 24"
              >
                <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
              </svg>
            </div>
          )}

          {/* Overlay hover */}
          <div className="absolute inset-0 bg-intercasa-dark/0 group-hover:bg-intercasa-dark/10 transition-colors duration-500" />
        </div>

        {/* Contenido de la tarjeta */}
        <div className="p-5 md:p-6 flex-1 flex flex-col">
          {/* Título */}
          <h3 className="font-baskerville text-lg md:text-xl font-bold text-intercasa-dark mb-4 group-hover:text-intercasa-red transition-colors duration-300 leading-tight flex-1">
            {title}
          </h3>

          {/* Botón Ver receta */}
          <div className="flex items-center text-intercasa-red font-lato font-semibold text-sm group-hover:translate-x-2 transition-transform duration-300">
            <span>Ver receta</span>
            <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </div>
        </div>

        {/* Barra roja de selección en hover */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-intercasa-red scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
      </article>
    </Link>
  );
};

/**
 * Página de Recetas con diseño inmersivo estilo Awwwards
 * @returns {JSX.Element} Página de recetas inmersiva
 */
const RecipesPageImmersive = () => {
  const heroRef = useRef(null);
  const recipes = getAllRecipes();
  const { abierto, abrir, cerrar } = useRecipeSubmissionModal();

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;

    const tl = gsap.timeline();
    
    // Animación de entrada del hero
    tl.fromTo(hero.querySelector('.hero-title'),
      { y: 40, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.8, ease: 'power3.out' }
    )
    .fromTo(hero.querySelector('.hero-subtitle'),
      { y: 30, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.6, ease: 'power3.out' },
      '-=0.4'
    )
    .fromTo(hero.querySelector('.hero-intro'),
      { y: 20, opacity: 0 },
      { y: 0, opacity: 1, duration: 0.5, ease: 'power3.out' },
      '-=0.3'
    );

    return () => tl.kill();
  }, []);

  return (
    <ImmersiveLayout>
      {/* 
        =============================================
        1. HERO - Imagen de plato apetecible
        Según estructura.md: Título "Recetas para disfrutar cada día"
        =============================================
      */}
      <section 
        ref={heroRef}
        className="relative min-h-[60vh] flex items-center overflow-hidden"
      >
        {/* Fondo oscuro base */}
        <div className="absolute inset-0 bg-intercasa-dark" />
        
        {/* Imagen de fondo con opacidad - Usando WebP para mejor rendimiento */}
        <div 
          className="absolute inset-0 opacity-70"
          style={{
            backgroundImage: 'url(/images/recipes/RECETAS.webp)',
            backgroundSize: 'cover',
            backgroundRepeat: 'no-repeat',
            backgroundPosition: 'center',
          }}
        />
        
        {/* Gradiente para mejor legibilidad */}
        <div className="absolute inset-0 bg-gradient-to-r from-intercasa-dark/90 via-intercasa-dark/70 to-intercasa-dark/90" />

        {/* Patrón decorativo SVG */}
        <div className="absolute inset-0 opacity-5">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <pattern id="recipe-pattern" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
                <circle cx="30" cy="30" r="2" fill="currentColor" className="text-intercasa-dark" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#recipe-pattern)" />
          </svg>
        </div>

        {/* Contenido del hero */}
        <div className="relative z-10 container mx-auto px-4 md:px-8 py-20">
          <div className="max-w-4xl mx-auto text-center">
            {/* Etiqueta superior */}
            <AnimatedSection animation="fadeUp" delay={0}>
              <span className="inline-block font-lato text-intercasa-yellow text-sm uppercase tracking-[0.2em] mb-6">
                Recetas que inspiran
              </span>
            </AnimatedSection>

            {/* Título principal H1 - según recetas.md */}
            <h1 className="hero-title font-baskerville font-bold text-white text-4xl md:text-5xl lg:text-6xl xl:text-7xl leading-[1.1] mb-6">
              Recetas para disfrutar{' '}
              <span className="text-intercasa-yellow">cada día</span>
            </h1>

            {/* Subtítulo - según recetas.md */}
            <p className="hero-subtitle font-lato text-white/90 text-lg md:text-xl lg:text-2xl leading-relaxed max-w-3xl mx-auto text-center">
              Ideas sencillas y sabrosas con tus productos Intercasa favoritos.
            </p>
          </div>
        </div>
      </section>

      {/* 
        =============================================
        2. INTRODUCCIÓN BREVE
        Párrafo que da contexto según recetas.md
        =============================================
      */}
      <section className="bg-white py-12 md:py-16">
        <div className="container mx-auto px-4 md:px-8">
          <AnimatedSection animation="fadeUp" delay={0}>
            <blockquote className="max-w-3xl mx-auto text-center">
              <p className="font-lato text-intercasa-dark/80 text-lg md:text-xl lg:text-2xl leading-relaxed italic">
                "Descubre preparaciones tradicionales, recetas caseras y propuestas de nuestros clientes 
                elaboradas con productos Intercasa. Cocinar con sabor canario nunca ha sido tan fácil."
              </p>
            </blockquote>
          </AnimatedSection>
        </div>
      </section>

      {/* 
        =============================================
        3. GRID PRINCIPAL DE RECETAS
        Cards con imagen destacada, título y botón "Ver receta"
        Grid 3 columnas en escritorio, 1-2 en móvil
        =============================================
      */}
      <section className="bg-intercasa-bone py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-8">

          {/* Grid de recetas */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {recipes.map((recipe, index) => (
              <AnimatedSection 
                key={recipe.id} 
                animation="fadeUp" 
                delay={0.1 + (index * 0.05)}
                className="h-full"
              >
                <RecipeCardImmersive {...recipe} />
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/*
        =============================================
        4. CTA - Envío de recetas de usuarios
        Sección a ancho completo siguiendo el patrón de ContactPage.jsx
        (líneas 99-141). Va justo después del grid de recetas y ANTES del
        bloque de cierre de marca: la invitación a participar aparece
        mientras el usuario todavía tiene las recetas en la cabeza, y el
        cierre de marca sigue siendo lo último que se lee antes del footer.
        =============================================
      */}
      <section className="bg-intercasa-red py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <AnimatedSection animation="fadeUp">
              <h2 className="font-baskerville font-bold text-white text-3xl md:text-4xl lg:text-5xl mb-6 leading-tight">
                Envíanos tu{' '}
                <span className="text-intercasa-yellow">receta</span>
              </h2>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.1}>
              <p className="text-white/80 text-lg leading-relaxed mb-10">
                Comparte con nosotros tu receta elaborada con productos Intercasa. Las mejores propuestas se publicarán en nuestra web y redes sociales.
              </p>
            </AnimatedSection>

            <AnimatedSection animation="fadeUp" delay={0.2}>
              <MagneticButton variant="secondary" size="lg" onClick={abrir}>
                Enviar mi receta
              </MagneticButton>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/*
        =============================================
        5. BLOQUE DE CIERRE - Identidad de marca
        Imagen + frase según recetas.md
        =============================================
      */}
      <section className="relative bg-intercasa-dark py-20 md:py-28 overflow-hidden">
        {/* Imagen de fondo de ingredientes o textura de salsa */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-intercasa-dark via-intercasa-dark/95 to-intercasa-dark" />
          {/* Patrón decorativo */}
          <div className="absolute inset-0 opacity-5">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <pattern id="close-pattern" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
                  <circle cx="20" cy="20" r="1.5" fill="currentColor" className="text-white" />
                </pattern>
              </defs>
              <rect width="100%" height="100%" fill="url(#close-pattern)" />
            </svg>
          </div>
        </div>

        <div className="relative z-10 container mx-auto px-4 md:px-8">
          <div className="max-w-4xl mx-auto text-center">
            {/* Icono decorativo */}
            <AnimatedSection animation="fadeUp" delay={0}>
              <div className="inline-flex items-center justify-center w-20 h-20 bg-intercasa-red/10 rounded-full mb-8">
                <svg className="w-10 h-10 text-intercasa-yellow" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M11 9H9V2H7v7H5V2H3v7c0 2.12 1.66 3.84 3.75 3.97V22h2.5v-9.03C11.34 12.84 13 11.12 13 9V2h-2v7zm5-3v8h2.5v8H21V2c-2.76 0-5 2.24-5 4z"/>
                </svg>
              </div>
            </AnimatedSection>

            {/* Frase de cierre según recetas.md */}
            <AnimatedSection animation="fadeUp" delay={0.1}>
              <h2 className="font-baskerville font-bold text-white text-3xl md:text-4xl lg:text-5xl mb-6 leading-tight">
                Crea, combina y disfruta{' '}
                <span className="text-intercasa-yellow">con Intercasa</span>
              </h2>
            </AnimatedSection>

            {/* CTA: Ver más recetas (en este caso llevamos a productos) */}
            <AnimatedSection animation="fadeUp" delay={0.2}>
              <Link to="/productos">
                <MagneticButton variant="primary" size="lg">
                  Descubre nuestros productos
                </MagneticButton>
              </Link>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Modal de envío de recetas: se abre en caliente desde el botón
          anterior o en frío al entrar directamente en /envio-receta (QR). */}
      <RecipeSubmissionModal abierto={abierto} onCerrar={cerrar} />
    </ImmersiveLayout>
  );
};

export default RecipesPageImmersive;
